// client/src/utils/rfqHeaderFactRows.ts
//
// The RFQ's header facts as the screens show them: the buyer's own RFQ / order number, port, dates and
// currency, read from the document at intake and stored on the RFQ (server: `src/utils/rfqHeaderFacts.js`
// reads them, `RFQ.buyerRef`... keeps them). The inbox RFQ panel and the quotation header both build
// their rows HERE, so a fact has one label and one place in the order on every screen.
//
// NOT a mirrored twin of the server file: the server READS a document and returns six strings; this
// file only LABELS and ORDERS them. The two must list the same six keys, and
// `src/__tests__/rfqHeaderFactKeysClientServer.test.js` fails if they ever differ.

export const RFQ_HEADER_FACT_KEYS = ['buyerRef', 'port', 'rfqDate', 'deliveryDate', 'eta', 'currency'] as const;

export type RfqHeaderFactKey = (typeof RFQ_HEADER_FACT_KEYS)[number];

/** Anything that carries the facts: a stored RFQ, the populated RFQ of a quotation. Every field optional. */
export type RfqHeaderFacts = Partial<Record<RfqHeaderFactKey, string | null | undefined>>;

export interface RfqHeaderFactRow {
  key: RfqHeaderFactKey;
  label: string;
  value: string;
  /** What the fact is, for a tooltip. */
  hint: string;
}

const LABELS: Record<RfqHeaderFactKey, { label: string; hint: string }> = {
  buyerRef: { label: 'Buyer ref', hint: "The number the buyer's own document gives this request (its RFQ, order or requisition number)" },
  port: { label: 'Port', hint: "The port named in the buyer's document" },
  rfqDate: { label: 'RFQ date', hint: "The date on the buyer's document, as written" },
  deliveryDate: { label: 'Delivery date', hint: 'The delivery or required date in the buyer\'s document, as written' },
  eta: { label: 'ETA', hint: "The vessel's arrival date in the buyer's document, as written" },
  currency: { label: 'Currency', hint: "The currency named in the buyer's document" },
};

/**
 * The facts the document stated, in the order a screen shows them: one row per fact that holds text,
 * none for an empty one (the document states none; nothing is invented to fill the place). Anything that
 * is not an object, or a fact that is not text, gives no row.
 */
export function rfqHeaderFactRows(source: unknown): RfqHeaderFactRow[] {
  if (!source || typeof source !== 'object') return [];
  const facts = source as Record<string, unknown>;
  const rows: RfqHeaderFactRow[] = [];
  for (const key of RFQ_HEADER_FACT_KEYS) {
    const raw = facts[key];
    const value = typeof raw === 'string' ? raw.trim() : '';
    if (value) rows.push({ key, value, ...LABELS[key] });
  }
  return rows;
}
